import { useCallback, useEffect, useMemo, useState } from "react";
import { Scale, Building2, ArrowRightLeft, FolderOpen, Loader2, Play, Save, Trash2, RotateCcw, Check, Plus, Wand2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { fmtDate, fmtRp, fmtRpShort } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { SectionCard, InfoNote } from "@/components/common/Cards";
import { CompanyPanel } from "@/components/equalizer/CompanyPanel";
import { EqualizerBoard, TransferDialog } from "@/components/equalizer/Board";
import { SimulationResult } from "@/components/equalizer/SimResult";
import { AutoOptimizeDialog } from "@/components/equalizer/AutoOptimizeDialog";
import { TX_LABEL, applyTransfersLocal, rid } from "@/components/equalizer/eqUtils";

const tabCls = "data-[state=active]:bg-primary data-[state=active]:text-primary-foreground px-4 py-2";

export default function EqualizerToolPage() {
  const [tab, setTab] = useState("data");
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [meta, setMeta] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [transfers, setTransfers] = useState([]);
  const [moveReq, setMoveReq] = useState(null);
  const [dragState, setDragState] = useState(null);
  const [result, setResult] = useState(null);
  const [resultKey, setResultKey] = useState("");
  const [simulating, setSimulating] = useState(false);
  const [scenario, setScenario] = useState({ id: null, name: "Skenario ekualisasi" });
  const [scenarios, setScenarios] = useState(null);
  const [saving, setSaving] = useState(false);
  const [optOpen, setOptOpen] = useState(false);

  const loadCompanies = useCallback(async () => {
    try {
      const { data } = await api.get("/equalizer/companies");
      setCompanies(data.items || []);
    } catch (e) {
      toast.error(errMsg(e, "Gagal memuat data PT"));
    } finally {
      setLoading(false);
    }
  }, []);
  const loadScenarios = useCallback(async () => {
    try {
      const { data } = await api.get("/equalizer/scenarios");
      setScenarios(data.items || []);
    } catch (e) {
      toast.error(errMsg(e, "Gagal memuat skenario"));
      setScenarios([]);
    }
  }, []);

  useEffect(() => {
    loadCompanies();
    api.get("/equalizer/meta").then(({ data }) => setMeta(data)).catch(() => {});
  }, [loadCompanies]);
  useEffect(() => {
    if (tab === "scenarios") loadScenarios();
  }, [tab, loadScenarios]);

  // keep selection valid if companies change
  useEffect(() => {
    const ids = new Set(companies.map((c) => c.id));
    setSelectedIds((prev) => prev.filter((id) => ids.has(id)));
    setTransfers((prev) => prev.filter((t) => ids.has(t.from_id) && ids.has(t.to_id)));
  }, [companies]);

  const selected = useMemo(() => selectedIds.map((id) => companies.find((c) => c.id === id)).filter(Boolean), [selectedIds, companies]);
  const preview = useMemo(() => applyTransfersLocal(selected, transfers), [selected, transfers]);
  const currentKey = JSON.stringify([selectedIds, transfers]);
  const stale = !!result && resultKey !== currentKey;
  const nameOf = (id) => companies.find((c) => c.id === id)?.name || "-";

  const toggle = (id) => {
    setSelectedIds((prev) => {
      if (prev.includes(id)) {
        setTransfers((ts) => ts.filter((t) => t.from_id !== id && t.to_id !== id));
        return prev.filter((x) => x !== id);
      }
      if (prev.length >= 10) {
        toast.info("Maksimal 10 PT dalam satu ekualizer.");
        return prev;
      }
      return [...prev, id];
    });
  };

  const addTransfer = (t) => {
    setTransfers((prev) => [...prev, { ...t, id: rid() }]);
    setMoveReq(null);
    toast.success(`${fmtRpShort(t.amount)} dipindah ke ${nameOf(t.to_id)}.`);
  };

  const simulate = async () => {
    if (selected.length < 2) {
      toast.info("Pilih minimal 2 PT.");
      return;
    }
    setSimulating(true);
    try {
      const { data } = await api.post("/equalizer/simulate", { company_ids: selectedIds, transfers, scenario_id: scenario.id || undefined });
      setResult(data);
      setResultKey(currentKey);
      const s = data.group.hemat_bersih;
      toast.success(s > 0 ? `Simulasi selesai: grup hemat ${fmtRpShort(s)}.` : s < 0 ? `Simulasi selesai: tambahan pajak ${fmtRpShort(-s)}.` : "Simulasi selesai.");
    } catch (e) {
      toast.error(errMsg(e, "Simulasi gagal"));
    } finally {
      setSimulating(false);
    }
  };

  const saveScenario = async () => {
    if (selected.length < 2) {
      toast.info("Pilih minimal 2 PT sebelum menyimpan.");
      return;
    }
    setSaving(true);
    try {
      const body = { name: scenario.name || "Skenario", company_ids: selectedIds, transfers, last_group: result && !stale ? result.group : null };
      const { data } = scenario.id ? await api.put(`/equalizer/scenarios/${scenario.id}`, body) : await api.post("/equalizer/scenarios", body);
      setScenario({ id: data.id, name: data.name });
      toast.success("Skenario disimpan.");
      setScenarios(null);
    } catch (e) {
      toast.error(errMsg(e, "Gagal menyimpan skenario"));
    } finally {
      setSaving(false);
    }
  };

  const openScenario = (s) => {
    const ids = s.company_ids.filter((id) => companies.some((c) => c.id === id));
    setSelectedIds(ids);
    setTransfers((s.transfers || []).filter((t) => ids.includes(t.from_id) && ids.includes(t.to_id)));
    setScenario({ id: s.id, name: s.name });
    setResult(null);
    setTab("board");
    toast.success(`Skenario "${s.name}" dibuka.`);
  };
  const deleteScenario = async (s) => {
    try {
      await api.delete(`/equalizer/scenarios/${s.id}`);
      setScenarios((prev) => (prev || []).filter((x) => x.id !== s.id));
      if (scenario.id === s.id) setScenario({ id: null, name: "Skenario ekualisasi" });
      toast.success("Skenario dihapus.");
    } catch (e) {
      toast.error(errMsg(e, "Gagal menghapus skenario"));
    }
  };
  const newScenario = () => {
    setScenario({ id: null, name: "Skenario ekualisasi" });
    setTransfers([]);
    setResult(null);
  };

  return (
    <div className="max-w-[1500px] mx-auto space-y-6" data-testid="equalizer-page">
      <div>
        <div className="eyebrow flex items-center gap-2"><Scale className="h-3.5 w-3.5" /> Perencanaan Pajak Grup</div>
        <h1 className="font-display text-4xl mt-1.5" data-testid="equalizer-title">Ekualizer Pajak Multi-PT</h1>
        <p className="text-sm text-muted-foreground mt-1.5 max-w-3xl leading-relaxed">
          Geser pendapatan (hijau) dan beban (merah) antar PT dalam satu grup, lalu simulasikan dampaknya pada PPh Badan, PPN, PPh 23 dan PPh final.
        </p>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-[hsl(220_12%_8%)] border border-border h-auto p-1 flex-wrap" data-testid="eq-tabs">
          <TabsTrigger value="data" className={tabCls} data-testid="eq-tab-data"><Building2 className="h-4 w-4 mr-1.5" /> Data PT</TabsTrigger>
          <TabsTrigger value="board" className={tabCls} data-testid="eq-tab-board"><ArrowRightLeft className="h-4 w-4 mr-1.5" /> Ekualizer</TabsTrigger>
          <TabsTrigger value="scenarios" className={tabCls} data-testid="eq-tab-scenarios"><FolderOpen className="h-4 w-4 mr-1.5" /> Skenario</TabsTrigger>
        </TabsList>

        <TabsContent value="data" className="mt-5">
          <CompanyPanel companies={companies} loading={loading} meta={meta} onReload={loadCompanies} />
        </TabsContent>

        <TabsContent value="board" className="mt-5 space-y-5">
          <SectionCard
            title="Pilih PT (2–10)"
            description="Klik nama PT untuk memasukkannya ke papan ekualizer."
            testid="eq-pick-card"
          >
            {companies.length < 2 ? (
              <div className="text-sm text-muted-foreground" data-testid="eq-pick-empty">
                Butuh minimal 2 PT.{" "}
                <Button variant="link" className="px-0 text-primary" onClick={() => setTab("data")} data-testid="eq-go-data">Tambah di Data PT</Button>
              </div>
            ) : (
              <div className="flex flex-wrap gap-2">
                {companies.map((c) => {
                  const on = selectedIds.includes(c.id);
                  return (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => toggle(c.id)}
                      aria-pressed={on}
                      className={`press inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border text-sm ${on ? "border-primary bg-[hsl(43_28%_9%)] text-primary" : "border-border bg-[hsl(220_12%_8%)] text-muted-foreground hover:text-foreground"}`}
                      data-testid={`eq-pick-${c.id}`}
                    >
                      {on ? <Check className="h-3.5 w-3.5" /> : <Plus className="h-3.5 w-3.5" />} {c.name}
                    </button>
                  );
                })}
              </div>
            )}
          </SectionCard>

          {selected.length >= 2 && (
            <>
              <div className="flex flex-wrap items-center gap-2" data-testid="eq-toolbar">
                <Input
                  value={scenario.name}
                  onChange={(e) => setScenario((s) => ({ ...s, name: e.target.value }))}
                  className="h-10 w-64 bg-[hsl(220_12%_8%)]"
                  aria-label="Nama skenario"
                  data-testid="eq-scenario-name"
                />
                <Button variant="outline" onClick={saveScenario} disabled={saving} data-testid="eq-scenario-save">
                  {saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Save className="h-4 w-4 mr-1.5" />} {scenario.id ? "Simpan perubahan" : "Simpan skenario"}
                </Button>
                {scenario.id && (
                  <Button variant="ghost" onClick={newScenario} data-testid="eq-scenario-new">Skenario baru</Button>
                )}
                <Button variant="ghost" onClick={() => setTransfers([])} disabled={!transfers.length} data-testid="eq-reset-transfers">
                  <RotateCcw className="h-4 w-4 mr-1.5" /> Reset pemindahan
                </Button>
                <Button variant="outline" onClick={() => setOptOpen(true)} className="ml-auto" data-testid="eq-auto-optimize">
                  <Wand2 className="h-4 w-4 mr-1.5" /> Rekomendasi otomatis
                </Button>
                <Button onClick={simulate} disabled={simulating} className="press h-11 px-6" data-testid="eq-simulate">
                  {simulating ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Play className="h-4 w-4 mr-2" />} Simulasi Pajak
                </Button>
              </div>

              <InfoNote>
                Seret kotak ke kolom PT lain, atau klik ikon panah di kotak. Kotak bergaris putus-putus adalah hasil pemindahan / transaksi antar-PT.
              </InfoNote>

              <EqualizerBoard companies={selected} preview={preview} onRequestMove={setMoveReq} dragState={dragState} setDragState={setDragState} />

              <SectionCard title={`Daftar pemindahan (${transfers.length})`} testid="eq-transfers-card">
                {transfers.length === 0 ? (
                  <p className="text-xs text-muted-foreground" data-testid="eq-transfers-empty">Belum ada pemindahan. Seret kotak pendapatan/beban ke PT lain.</p>
                ) : (
                  <ul className="space-y-2">
                    {transfers.map((t, i) => (
                      <li key={t.id} className="flex flex-wrap items-center gap-2 rounded-lg border border-border bg-[hsl(220_10%_7%)] px-3 py-2 text-sm" data-testid={`eq-transfer-${i}`}>
                        <span className={t.section === "pendapatan" ? "text-[hsl(160_55%_62%)]" : "text-[hsl(0_72%_68%)]"}>{t.item_name}</span>
                        <span className="text-muted-foreground">{nameOf(t.from_id)} → {nameOf(t.to_id)}</span>
                        <span className="num text-primary">{fmtRp(t.amount)}</span>
                        <Badge variant="outline" className="text-[10px] border-border">{t.mode === "tagihan" ? `Tagihan · ${TX_LABEL[t.tx_type]}` : "Realokasi"}</Badge>
                        {t.note && <span className="text-[11px] text-muted-foreground">“{t.note}”</span>}
                        <Button size="icon" variant="ghost" className="ml-auto h-8 w-8 text-destructive hover:text-destructive" onClick={() => setTransfers((prev) => prev.filter((x) => x.id !== t.id))} aria-label="Hapus pemindahan" data-testid={`eq-transfer-${i}-delete`}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </li>
                    ))}
                  </ul>
                )}
              </SectionCard>

              <SimulationResult result={result} stale={stale} />
            </>
          )}
        </TabsContent>

        <TabsContent value="scenarios" className="mt-5">
          <SectionCard title="Skenario tersimpan" description="Buka kembali kombinasi PT dan pemindahan yang pernah disimpan." testid="eq-scenarios-card">
            {scenarios === null ? (
              <p className="text-xs text-muted-foreground">Memuat…</p>
            ) : scenarios.length === 0 ? (
              <div className="rounded-xl border border-border bg-[hsl(220_10%_7%)] p-8 text-center text-sm text-muted-foreground" data-testid="eq-scenarios-empty">
                <FolderOpen className="h-6 w-6 mx-auto mb-2 text-primary/70" /> Belum ada skenario tersimpan.
              </div>
            ) : (
              <ul className="space-y-2" data-testid="eq-scenarios-list">
                {scenarios.map((s) => {
                  const g = s.last_result?.group;
                  return (
                    <li key={s.id} className="rounded-xl border border-border bg-[hsl(220_10%_7%)] p-4 flex flex-wrap items-center gap-3" data-testid={`eq-scenario-${s.id}`}>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium">{s.name}</div>
                        <div className="text-[11px] text-muted-foreground mt-0.5">
                          {s.company_ids.map(nameOf).join(" · ")} · {(s.transfers || []).length} pemindahan · {fmtDate(s.updated_at)}
                        </div>
                      </div>
                      {g && (
                        <Badge variant="outline" className={g.hemat_bersih >= 0 ? "border-[hsl(160_45%_35%)] text-[hsl(160_55%_62%)]" : "border-[hsl(0_45%_35%)] text-[hsl(0_72%_68%)]"}>
                          {g.hemat_bersih >= 0 ? `Hemat ${fmtRpShort(g.hemat_bersih)}` : `Tambahan ${fmtRpShort(-g.hemat_bersih)}`}
                        </Badge>
                      )}
                      <Button size="sm" className="press" onClick={() => openScenario(s)} data-testid={`eq-scenario-${s.id}-open`}>Buka</Button>
                      <Button size="sm" variant="ghost" className="text-destructive hover:text-destructive" onClick={() => deleteScenario(s)} aria-label="Hapus skenario" data-testid={`eq-scenario-${s.id}-delete`}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </li>
                  );
                })}
              </ul>
            )}
          </SectionCard>
        </TabsContent>
      </Tabs>

      {moveReq && (
        <TransferDialog
          key={`${moveReq.fromId}-${moveReq.itemId}-${moveReq.toId}`}
          request={moveReq}
          companies={selected}
          preview={preview}
          onClose={() => setMoveReq(null)}
          onConfirm={addTransfer}
        />
      )}

      <AutoOptimizeDialog
        open={optOpen}
        onOpenChange={setOptOpen}
        companyIds={selectedIds}
        selectedCompanies={selected}
        onApply={(newTransfers) => {
          setTransfers(newTransfers.map((t) => ({ ...t, id: t.id || rid() })));
          setResult(null);
        }}
      />
    </div>
  );
}
